import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, Upload, Sparkles, X, Mail, Loader2, ExternalLink } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  PAGE_WIDE, Dialog, Field, SearchInput, Switch, EmptyState, ErrorState, TableSkeleton, CopyButton, PersonAvatar, fmtDate, plural,
} from '../../components/ui';
import { ExpiryBadge, IconButton, expiryStatus, docHref, docName, staffName, roleLabel, useStableCallback } from './hrKit';

const MAX_MB = 15;
const extOf = (name = '') => { const p = name.split('.'); return p.length > 1 ? p[p.length - 1].toUpperCase() : 'FILE'; };

export default function StaffDocumentShared() {
  const [docs, setDocs] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sortAlpha, setSortAlpha] = useState(false);
  const [groupByStaff, setGroupByStaff] = useState(true);
  const [qualDefinitionId, setQualDefinitionId] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [search, setSearch] = useState('');
  const [lastAi, setLastAi] = useState(null);
  const [reanalysing, setReanalysing] = useState({});
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderDraft, setReminderDraft] = useState(null);
  const [reminderError, setReminderError] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const fileInputRef = useRef(null);
  const closeReminders = useStableCallback(() => { setReminderOpen(false); setReminderDraft(null); setReminderError(null); });

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    Promise.all([api.get('/staff/documents'), api.get('/staff'), api.get('/smart-match').catch(() => ({ data: {} }))])
      .then(([d, s, sm]) => {
        const data = d.data;
        setDocs(Array.isArray(data) ? data : (data.documents || data.data || []));
        setStaffList(Array.isArray(s.data) ? s.data : []);
        setQualifications(sm.data?.qualifications || []);
      })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load documents. Try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const qualsByCategory = useMemo(() => {
    const map = new Map();
    for (const q of qualifications) {
      const cat = q.category_name || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(q);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [qualifications]);

  const reanalyse = async (d) => {
    setReanalysing((r) => ({ ...r, [d.id]: true }));
    const toastId = toast.loading('Reading the document again…');
    try {
      const { data } = await api.post(`/staff/documents/${d.id}/reanalyse`);
      const ai = data.aiAnalysis;
      const bits = [];
      if (ai?.documentType) bits.push(ai.documentType);
      if (ai?.expiryDate) bits.push(`expires ${fmtDate(ai.expiryDate)}`);
      if (data.createdQualification) bits.push('qualification added');
      toast.success(`Read: ${bits.join(' · ') || 'nothing new found'}`, { id: toastId, duration: 5000 });
      setLastAi({ ...ai, fileName: docName(d), createdQualification: data.createdQualification });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "The document couldn't be read again. Try later.", { id: toastId });
    } finally {
      setReanalysing((r) => ({ ...r, [d.id]: false }));
    }
  };

  const draftReminders = async () => {
    const expiring = docs.filter((d) => expiryStatus(d.expiry_date).state === 'soon');
    if (!expiring.length) { toast.error('No documents expire in the next 30 days'); return; }
    const byStaff = {};
    for (const d of expiring) {
      const s = staffList.find((x) => x.id === d.staff_id);
      const name = s ? staffName(s) : (d.staff_name || 'Unknown staff member');
      (byStaff[name] = byStaff[name] || []).push({ name: d.document_type || d.category || docName(d) || 'Document', expiryDate: String(d.expiry_date).slice(0, 10) });
    }
    setDrafting(true); setReminderOpen(true); setReminderDraft(null); setReminderError(null);
    try {
      const drafts = await Promise.all(Object.entries(byStaff).map(async ([recipientName, documents]) => {
        const { data } = await api.post('/ai/draft-reminder', { recipientName, recipientType: 'staff member', documents });
        return { recipientName, documents, emailBody: data.emailBody };
      }));
      setReminderDraft(drafts);
    } catch (e) { setReminderError(e.response?.data?.message || "The reminders couldn't be drafted. Try again."); }
    finally { setDrafting(false); }
  };

  const shown = useMemo(() => {
    if (!selectedStaff) return [];
    let list = docs.filter((d) => d.staff_id === selectedStaff);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((d) => `${docName(d)} ${d.category || ''}`.toLowerCase().includes(q));
    if (sortAlpha) list = [...list].sort((a, b) => docName(a).localeCompare(docName(b)));
    return list;
  }, [docs, sortAlpha, search, selectedStaff]);

  const groups = useMemo(() => {
    if (!groupByStaff) return [{ key: 'all', staffId: null, name: null, docs: shown }];
    const map = new Map();
    for (const d of shown) {
      const k = d.staff_id || 'unassigned';
      if (!map.has(k)) map.set(k, { key: k, staffId: d.staff_id, name: d.staff_name || 'Not linked to anyone', first: d.staff_first_name, last: d.staff_last_name, docs: [] });
      map.get(k).docs.push(d);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [shown, groupByStaff]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!selectedStaff) { toast.error('Choose a staff member first'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`The file is larger than ${MAX_MB} MB`); return; }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('staffId', selectedStaff);
    if (qualDefinitionId) {
      formData.append('qualDefinitionId', qualDefinitionId);
      const sel = qualifications.find((q) => q.id === qualDefinitionId);
      if (sel) { formData.append('qualificationName', sel.name); formData.append('category', sel.category_name || 'Qualification'); }
    }
    const toastId = toast.loading('Uploading and reading the document…');
    setUploading(true);
    try {
      const { data } = await api.post('/staff/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ai = data.aiAnalysis;
      if (ai) {
        const bits = [];
        if (ai.documentType) bits.push(ai.documentType);
        if (ai.expiryDate) bits.push(`expires ${fmtDate(ai.expiryDate)}`);
        if (data.createdQualification) bits.push('qualification added');
        toast.success(`Uploaded. Read: ${bits.join(' · ') || 'no details found'}`, { id: toastId, duration: 5000 });
      } else if (data.aiError) {
        toast.success("Uploaded. The document couldn't be read automatically, so check its category and expiry.", { id: toastId, duration: 6000 });
      } else {
        toast.success('Uploaded', { id: toastId });
      }
      setLastAi(ai ? { ...ai, fileName: file.name, createdQualification: data.createdQualification } : null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const selected = staffList.find((s) => s.id === selectedStaff);
  const box = (child) => <div className="rounded-xl border border-slate-200 bg-white">{child}</div>;

  return (
    <div className={PAGE_WIDE}>
      {/* Upload */}
      <section aria-labelledby="hub-upload-h" className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 id="hub-upload-h" className="text-[15px] font-semibold text-slate-900">Upload a staff document</h2>
          <span className="text-[12.5px] text-slate-500">We read the type and expiry date, and can add the qualification to their record.</span>
          <button type="button" onClick={draftReminders} disabled={drafting || loading} className="btn-secondary btn-sm ml-auto"
            title="Draft reminder emails for staff with documents expiring in the next 30 days">
            {drafting ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Mail size={13} aria-hidden="true" />}Draft expiry reminders
          </button>
        </div>
        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-12">
          <Field label="Staff member" required hint="Also filters the list below." className="md:col-span-5">
            {(id) => (
              <select id={id} value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)} className="input-field">
                <option value="">Choose…</option>
                {staffList.map((s) => <option key={s.id} value={s.id}>{staffName(s)}{s.role ? ` (${roleLabel(s.role)})` : ''}</option>)}
              </select>
            )}
          </Field>
          <Field label="Qualification" hint="Leave blank and we'll work it out." className="md:col-span-4">
            {(id) => (
              <select id={id} value={qualDefinitionId} onChange={(e) => setQualDefinitionId(e.target.value)} className="input-field">
                <option value="">Work it out from the document</option>
                {qualsByCategory.map(([cat, items]) => (
                  <optgroup key={cat} label={cat}>{items.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}</optgroup>
                ))}
              </select>
            )}
          </Field>
          <div className="md:col-span-3">
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.txt,image/*" />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || !selectedStaff} className="btn-primary w-full justify-center"
              title={!selectedStaff ? 'Choose a staff member first' : undefined}>
              {uploading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Upload size={14} aria-hidden="true" />}
              {uploading ? 'Uploading…' : 'Upload document'}
            </button>
            <p className="mt-1 text-[12px] text-slate-500">PDF, Word or photo, up to {MAX_MB} MB.</p>
          </div>
        </div>
      </section>

      {/* What the last upload read */}
      {lastAi && (
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <Sparkles size={16} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-slate-900">Read from {lastAi.fileName}</p>
            <dl className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
              {[['Type', lastAi.documentType], ['Holder', lastAi.holderName], ['Reference', lastAi.referenceNumber],
                ['Expires', lastAi.expiryDate && fmtDate(lastAi.expiryDate)], ['Issued', lastAi.issueDate && fmtDate(lastAi.issueDate)],
                ['Issuer', lastAi.issuer], ['Country', lastAi.country], ['Category', lastAi.category]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="inline text-slate-500">{k}: </dt><dd className="inline font-medium text-slate-800">{v}</dd></div>
              ))}
            </dl>
            {lastAi.createdQualification && <p className="mt-1.5 text-[13px] text-emerald-700">Qualification “{lastAi.createdQualification.qualification_name}” added to their record.</p>}
            {lastAi.notes && <p className="mt-1 text-[12.5px] text-slate-600">{lastAi.notes}</p>}
          </div>
          <IconButton label="Dismiss" icon={X} onClick={() => setLastAi(null)} />
        </div>
      )}

      {/* View controls */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <label className="inline-flex items-center gap-2 text-[13px] text-slate-700"><Switch checked={groupByStaff} onChange={setGroupByStaff} label="Group by staff member" />Group by staff member</label>
        <label className="inline-flex items-center gap-2 text-[13px] text-slate-700"><Switch checked={sortAlpha} onChange={setSortAlpha} label="Sort A to Z" />Sort A to Z</label>
        {selected && <Link to={`/staff/${selected.id}/documents`} className="text-[13px] font-medium text-primary-700 hover:underline">Open {selected.first_name}'s Documents tab</Link>}
        <SearchInput value={search} onChange={setSearch} placeholder="Search documents" className="w-full sm:ml-auto sm:w-60" />
      </div>

      {loading ? box(<TableSkeleton rows={4} cols={4} />)
        : loadError ? box(<ErrorState message={loadError} onRetry={load} />)
          : !selectedStaff ? box(<EmptyState icon={FolderOpen} title="Choose a staff member" hint="Their documents appear here. Use the Staff member list above." />)
            : shown.length === 0 ? box(<EmptyState icon={FolderOpen} title={search ? 'No documents match' : 'No documents for this staff member yet'} hint={search ? 'Try clearing the search.' : 'Upload one above.'} />)
              : (
                <div className="space-y-4">
                  {groups.map((g) => (
                    <section key={g.key} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                      {g.name && (
                        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
                          <PersonAvatar id={g.staffId} firstName={g.first} lastName={g.last} name={g.name} size="sm" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900">{g.name}</p>
                            <p className="text-[12px] text-slate-500">{plural(g.docs.length, 'document')}</p>
                          </div>
                          {g.docs.some((d) => expiryStatus(d.expiry_date).state === 'expired') && <span className="badge-red">Has expired documents</span>}
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-[13px]">
                          <thead>
                            <tr className="border-b border-slate-200 text-left text-[12px] text-slate-500">
                              <th className="px-4 py-2 font-medium">Document</th><th className="px-4 py-2 font-medium">Category</th>
                              <th className="px-4 py-2 font-medium">Expiry</th><th className="px-4 py-2 font-medium">Uploaded</th>
                              <th className="px-4 py-2"><span className="sr-only">Actions</span></th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.docs.map((d) => {
                              const href = docHref(d);
                              const needsAi = !d.expiry_date || !d.category || d.category === 'Shared';
                              return (
                                <tr key={d.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                                  <td className="px-4 py-2.5">
                                    <span className="flex items-center gap-2">
                                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-slate-600">{extOf(d.name || d.filename)}</span>
                                      {href ? <a href={href} target="_blank" rel="noreferrer" className="font-medium text-slate-900 hover:text-primary-700 hover:underline">{docName(d) || 'Unknown'}</a>
                                        : <span className="font-medium text-slate-900">{docName(d) || 'Unknown'}</span>}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-slate-700">{d.category && d.category !== 'Shared' ? d.category : <span className="text-slate-400">—</span>}</td>
                                  <td className="px-4 py-2.5"><ExpiryBadge date={d.expiry_date} /></td>
                                  <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-slate-600">{d.created_at ? fmtDate(d.created_at) : '—'}</td>
                                  <td className="px-4 py-2.5">
                                    <span className="flex items-center justify-end">
                                      {href && <IconButton label={`Open ${docName(d)}`} icon={ExternalLink} href={href} target="_blank" rel="noreferrer" />}
                                      <IconButton label={needsAi ? `Read ${docName(d)} again to fill its category and expiry` : `Read ${docName(d)} again`}
                                        icon={Sparkles} onClick={() => reanalyse(d)} disabled={!!reanalysing[d.id]}
                                        className={needsAi ? '!text-primary-700' : ''} />
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ))}
                </div>
              )}

      <Dialog open={reminderOpen} onClose={closeReminders} size="lg" title="Expiry reminder emails"
        description="Drafted for each staff member with documents expiring in the next 30 days. Copy them into your email."
        footer={<button type="button" onClick={closeReminders} className="btn-secondary">Close</button>}>
        {drafting && <div className="space-y-2" aria-busy="true"><p className="text-sm text-slate-500">Drafting…</p><TableSkeleton rows={3} cols={2} /></div>}
        {!drafting && reminderError && <ErrorState message={reminderError} onRetry={draftReminders} />}
        {!drafting && reminderDraft && (
          <ul className="space-y-4">
            {reminderDraft.map((draft) => (
              <li key={draft.recipientName} className="overflow-hidden rounded-xl border border-slate-200">
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{draft.recipientName}</p>
                    <p className="text-[12px] text-slate-500">{draft.documents.map((d) => `${d.name} (expires ${fmtDate(d.expiryDate)})`).join(' · ')}</p>
                  </div>
                  <CopyButton value={draft.emailBody} label="Copy" title="Copy email" />
                </div>
                <pre className="whitespace-pre-wrap p-4 font-sans text-sm leading-relaxed text-slate-700">{draft.emailBody}</pre>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </div>
  );
}
